import { PDFDocument } from 'pdf-lib'
import { useEffect, useRef, useState } from 'react'

import UploadIcon from '@/components/icons/upload.svg?react'
import { PdfOrImageViewer } from '@/components/PdfOrImageViewer'
import { Button } from '@/components/ui/button'
import { getFullUrl } from '@/helpers/files'

interface Props {
  onChangeFile: (file: File | null) => void
  previewUrl?: string
  maxFileCount?: number
  helperImageUrl?: string
}

type FilePreview = {
  file: File
  preview: string | null // Blob URL for images, null for PDFs
}

export const UploadArea = ({ onChangeFile, previewUrl, maxFileCount = 10, helperImageUrl }: Props) => {
  // ===== State =====
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([])
  const [mergedFile, setMergedFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isMerging, setIsMerging] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isPreviewUrlHidden, setIsPreviewUrlHidden] = useState(false)
  // Cache-bust so the browser fetches fresh receipt when same URL was overwritten (e.g. after replacing receipt).
  const [mountCacheBust] = useState(() => Date.now())

  const fileInputRef = useRef<HTMLInputElement>(null)

  // ===== Computed Values =====
  const showPreview = uploadedFiles.length > 0 || (previewUrl && !isPreviewUrlHidden)

  // ===== Effects =====

  /** Cleanup blob URLs when component unmounts to prevent memory leaks */
  useEffect(() => {
    return () => {
      filePreviews.forEach(({ preview }) => {
        if (preview?.startsWith('blob:')) {
          URL.revokeObjectURL(preview)
        }
      })
    }
  }, [filePreviews])

  // ===== PDF Processing Functions =====

  /** Convert image file (JPG/PNG) to PDF format */
  const imageToPdf = async (file: File): Promise<Uint8Array> => {
    const pdfDoc = await PDFDocument.create()
    const imageBytes = await file.arrayBuffer()

    let image: Awaited<ReturnType<typeof pdfDoc.embedJpg>> | null = null

    // Try to embed based on file type, with fallback for mismatched MIME types
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      try {
        image = await pdfDoc.embedJpg(imageBytes)
      } catch (error) {
        // If JPEG embedding fails, try PNG (file might have wrong MIME type)
        try {
          image = await pdfDoc.embedPng(imageBytes)
        } catch {
          throw new Error(`Failed to embed image: ${error instanceof Error ? error.message : 'Invalid image format'}`)
        }
      }
    } else if (file.type === 'image/png') {
      try {
        image = await pdfDoc.embedPng(imageBytes)
      } catch (error) {
        // If PNG embedding fails, try JPEG (file might have wrong MIME type)
        try {
          image = await pdfDoc.embedJpg(imageBytes)
        } catch {
          throw new Error(`Failed to embed image: ${error instanceof Error ? error.message : 'Invalid image format'}`)
        }
      }
    } else {
      // Unknown type - try both formats
      try {
        image = await pdfDoc.embedPng(imageBytes)
      } catch {
        try {
          image = await pdfDoc.embedJpg(imageBytes)
        } catch {
          throw new Error(`Unsupported image type: ${file.type}. Only JPEG and PNG are supported.`)
        }
      }
    }

    if (!image) {
      throw new Error(`Failed to process image: ${file.name}`)
    }

    // Create page matching image dimensions
    const page = pdfDoc.addPage([image.width, image.height])
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height })

    return await pdfDoc.save()
  }

  /** Merge multiple files (PDFs and images) into a single PDF */
  const mergeFilesToPdf = async (files: File[]): Promise<File> => {
    const mergedPdf = await PDFDocument.create()

    // Process each file: convert images to PDF, then merge all pages
    for (const file of files) {
      const pdfBytes = file.type === 'application/pdf' ? await file.arrayBuffer() : await imageToPdf(file)
      const pdf = await PDFDocument.load(pdfBytes)
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
      pages.forEach(page => mergedPdf.addPage(page))
    }

    const mergedBytes = await mergedPdf.save()
    const blob = new Blob([new Uint8Array(mergedBytes)], { type: 'application/pdf' })
    return new File([blob], 'receipts.pdf', { type: 'application/pdf' })
  }

  // ===== Validation & File Handlers =====

  /** Validate file type - only images (JPG/PNG) and PDFs allowed */
  const validateFile = (file: File): boolean => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    return allowedTypes.includes(file.type)
  }

  /** Handle new file selection - validate, create previews, and merge into single PDF */
  const handleFilesSelect = async (newFiles: FileList | File[]) => {
    const filesArray = Array.from(newFiles)
    const validFiles: File[] = []
    const invalidFiles: string[] = []

    // Separate valid and invalid files
    filesArray.forEach(file => {
      if (validateFile(file)) {
        validFiles.push(file)
      } else {
        invalidFiles.push(file.name)
      }
    })

    // Handle validation errors
    if (invalidFiles.length > 0) {
      setErrorMessage(`קבצים לא תקינים (רק תמונות ו-PDF מותרים): ${invalidFiles.join(', ')}`)
      if (validFiles.length === 0) return
    } else {
      setErrorMessage('')
    }

    setIsPreviewUrlHidden(true) // Hide existing preview URL when adding new files
    const allFiles = [...uploadedFiles, ...validFiles]
    setUploadedFiles(allFiles)

    // Create blob URLs for image previews
    const newPreviews: FilePreview[] = validFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }))
    setFilePreviews([...filePreviews, ...newPreviews])

    // Merge all files into a single PDF
    try {
      setIsMerging(true)
      const merged = await mergeFilesToPdf(allFiles)
      setMergedFile(merged)
      onChangeFile(merged)
      if (invalidFiles.length === 0) setErrorMessage('')
    } catch (error) {
      console.error('Merge error:', error)
      setErrorMessage(`שגיאה במיזוג הקבצים: ${error instanceof Error ? error.message : 'נסה שוב'}`)
    } finally {
      setIsMerging(false)
    }
  }

  // ===== Event Handlers =====

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files?.length) handleFilesSelect(files)
    event.target.value = '' // Reset input for re-selection
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files.length) handleFilesSelect(e.dataTransfer.files)
  }

  const handleClick = () => fileInputRef.current?.click()

  /** Remove a specific file from the list and re-merge remaining files */
  const handleRemoveFile = async (indexToRemove: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== indexToRemove)
    const newPreviews = filePreviews.filter((_, i) => i !== indexToRemove)

    // Cleanup removed preview blob URL
    const removed = filePreviews[indexToRemove]
    if (removed?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(removed.preview)
    }

    setUploadedFiles(newFiles)
    setFilePreviews(newPreviews)

    if (newFiles.length === 0) {
      setMergedFile(null)
      onChangeFile(null)
    } else {
      // Re-merge remaining files
      try {
        setIsMerging(true)
        const merged = await mergeFilesToPdf(newFiles)
        setMergedFile(merged)
        onChangeFile(merged)
      } catch (error) {
        console.error('Re-merge error:', error)
        setErrorMessage('שגיאה במיזוג הקבצים. נסה שוב.')
      } finally {
        setIsMerging(false)
      }
    }
  }

  /** Remove all files and reset the component */
  const handleRemoveAll = () => {
    filePreviews.forEach(({ preview }) => {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview)
    })

    setUploadedFiles([])
    setFilePreviews([])
    setMergedFile(null)
    setErrorMessage('')
    setIsPreviewUrlHidden(true) // Hide existing preview URL
    onChangeFile(null)
  }

  // ===== Render =====
  const previewFullUrl = getFullUrl(previewUrl)
  return (
    <div className='space-y-4'>
      {/* Hidden file input - always available for "הוסף עוד" button */}
      <input ref={fileInputRef} type='file' accept='image/*,.pdf' multiple onChange={handleFileUpload} className='hidden' />

      {/* Error Notification - Shows validation and merge errors */}
      {errorMessage && (
        <div className='rounded-lg border border-red-200 bg-red-50 p-3'>
          <p className='text-sm text-red-800'>{errorMessage}</p>
        </div>
      )}

      {/* Upload Drop Zone - Shown when no files uploaded */}
      {!showPreview && (
        <div
          className={`border-border-default flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-white p-8 text-center transition-colors ${
            isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          {helperImageUrl ? (
            <img src={helperImageUrl} alt='Helper image' className='w-42 object-cover' />
          ) : (
            <UploadIcon className={`h-6 w-6 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          )}
          <p className='text-text-secondary text-sm'>גרור לכאן קבצים או לחץ לבחירה</p>
          {maxFileCount > 1 ? (
            <p className='text-text-placeholder text-xs'>ניתן להעלות מספר תמונות או PDF - יאוחדו לקובץ אחד</p>
          ) : (
            <p className='text-text-placeholder text-xs'>ניתן להעלות קובץ תמונה או PDF</p>
          )}
        </div>
      )}

      {/* Preview Section - Shows uploaded files or existing preview URL */}
      {showPreview && (
        <div className='rounded-lg bg-white p-4 shadow-sm'>
          {/* Header: File count, merge status, action buttons */}
          <div className='mb-3 flex items-center justify-between'>
            <div className='grow'>
              {uploadedFiles.length > 0 && (
                <div>
                  <p className='text-sm font-medium'>
                    {uploadedFiles.length} {uploadedFiles.length === 1 ? 'קובץ נבחר' : 'קבצים נבחרו'}
                  </p>
                  {mergedFile && <p className='text-xs text-gray-500'>גודל סופי: {(mergedFile.size / 1024 / 1024).toFixed(2)} MB</p>}
                  {isMerging && <p className='text-xs text-blue-600'>מאחד קבצים...</p>}
                </div>
              )}
            </div>
            <div className='flex gap-2'>
              {uploadedFiles.length < maxFileCount && (
                <Button variant='outline' size='sm' onClick={handleClick}>
                  הוספת קבלה
                </Button>
              )}
              {uploadedFiles.length > 0 && (
                <Button variant='outline' size='sm' onClick={handleRemoveAll}>
                  הסר {uploadedFiles.length > 1 ? 'הכל' : 'קובץ'}
                </Button>
              )}
            </div>
          </div>

          {/* List View - Multiple files */}
          {filePreviews.length > 1 && (
            <div className='mb-4 space-y-2'>
              {filePreviews.map((item, index) => (
                <div key={index} className='flex items-center gap-3 rounded border p-2'>
                  <div className='flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded border bg-gray-50'>
                    {item.preview ? (
                      <img src={item.preview} alt={item.file.name} className='h-full w-full object-cover' />
                    ) : (
                      <span className='text-xs text-gray-400'>PDF</span>
                    )}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm font-medium'>{item.file.name}</p>
                    <p className='text-xs text-gray-500'>{(item.file.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <Button variant='ghost' size='sm' onClick={() => handleRemoveFile(index)} className='flex-shrink-0'>
                    ✕
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Large Preview - Single file (image or PDF) */}
          {filePreviews.length === 1 && (
            <div className='mb-4'>
              <PdfOrImageViewer
                source={{ file: filePreviews[0].file }}
                id='uploaded-file-viewer'
                fileName={filePreviews[0].file.name}
                alt='File preview'
              />
            </div>
          )}

          {/* Existing File Preview - From server (when editing) */}
          {previewFullUrl && (
            <PdfOrImageViewer
              source={{ url: previewFullUrl }}
              id='preview-url-viewer'
              fileName='document.pdf'
              alt='File preview'
              cacheBust={mountCacheBust}
            />
          )}
        </div>
      )}
    </div>
  )
}
